import * as React from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getPostCheckoutSurveyOptions, updatePostCheckoutSurveyOption } from '../api';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { Dropdown } from '../component/Dropdown';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { countryOptions } from '../dropdownOptions';
import { Option, PostCheckoutSurveyOptionStatus, PostCheckoutSurveyOption } from '../pages/types';
import { generateErrorMsg } from '../utils';
import { STATUS_OPTIONS } from '../utils/postCheckoutSurveyUtils';

const PPS_TOAST = { position: 'bottom-left' as const, hideProgressBar: true };

export const EditPostCheckoutSurveyOptionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const stateOption = location.state?.option as PostCheckoutSurveyOption | undefined;

  const [option, setOption] = useState<PostCheckoutSurveyOption | undefined>(stateOption);
  const [notFound, setNotFound] = useState(false);
  const [optionLabel, setOptionLabel] = useState(stateOption?.label ?? '');
  const [marketingSource, setMarketingSource] = useState(stateOption?.marketing_source ?? '');
  const [marketingBucket, setMarketingBucket] = useState(stateOption?.marketing_bucket ?? '');
  const [country, setCountry] = useState<Option | undefined>(
    stateOption?.country ? countryOptions.find((o) => o.value === stateOption.country) : undefined
  );
  const [optionStatus, setOptionStatus] = useState<Option | undefined>(
    stateOption?.status ? STATUS_OPTIONS.find((o) => o.value === stateOption.status) : undefined
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (stateOption || !id) return;
    getPostCheckoutSurveyOptions().then((options) => {
      const found = options.find((o) => o.id === id);
      if (found) {
        setOption(found);
        setOptionLabel(found.label ?? '');
        setMarketingSource(found.marketing_source ?? '');
        setMarketingBucket(found.marketing_bucket ?? '');
        setCountry(countryOptions.find((o) => o.value === found.country));
        setOptionStatus(STATUS_OPTIONS.find((o) => o.value === found.status));
      } else {
        setNotFound(true);
      }
    });
  }, [id, stateOption]);

  if (notFound) return <Navigate to="/post-checkout-survey-options" replace />;
  if (!option) return null;

  const hasChanges =
    optionLabel !== (option.label || '') ||
    marketingSource !== (option.marketing_source ?? '') ||
    marketingBucket !== (option.marketing_bucket ?? '') ||
    optionStatus?.value !== option.status;

  const handleConfirm = async () => {
    if (!id) return;
    setIsSubmitting(true);
    try {
      await updatePostCheckoutSurveyOption(id, {
        label: optionLabel,
        marketing_source: marketingSource,
        marketing_bucket: marketingBucket,
        ...(optionStatus?.value && {
          status: optionStatus.value as PostCheckoutSurveyOptionStatus,
        }),
      });
      toast.info(
        <div>
          <strong>Success!</strong>
          <p>"{option.label}" has been updated.</p>
        </div>,
        PPS_TOAST
      );
      navigate('/post-checkout-survey-options');
    } catch (e) {
      toast.error(generateErrorMsg(e), PPS_TOAST);
    } finally {
      setIsSubmitting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="relative h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Edit option</h1>
        <div className="border rounded-lg p-6 space-y-4">
          <div>
            <div className="font-semibold mb-1">
              Name<span className="text-red-500">*</span>
            </div>
            <TextInput value={optionLabel} setTextChange={setOptionLabel} />
          </div>
          <div>
            <div className="font-semibold mb-1">Source</div>
            <TextInput value={marketingSource} setTextChange={setMarketingSource} />
          </div>
          <div>
            <div className="font-semibold mb-1">Bucket</div>
            <TextInput value={marketingBucket} setTextChange={setMarketingBucket} />
          </div>
          <div>
            <div className="font-semibold mb-1">
              Country<span className="text-red-500">*</span>
            </div>
            <Dropdown
              options={countryOptions}
              selectedOption={country}
              setSelectedOption={setCountry}
              defaultText="Select a country"
              noDefaultSelection
              isDisabled
            />
          </div>
          <div>
            <div className="font-semibold mb-1">
              Status<span className="text-red-500">*</span>
            </div>
            <Dropdown
              options={STATUS_OPTIONS}
              selectedOption={optionStatus}
              setSelectedOption={setOptionStatus}
              defaultText="Select a status"
              noDefaultSelection
            />
          </div>
        </div>
        <div className="mt-6 flex justify-center">
          <Button
            label="Confirm"
            variant="primary"
            isDisabled={isSubmitting || !hasChanges}
            onClick={() => setShowConfirm(true)}
          />
        </div>

        {showConfirm && (
          <ConfirmationModal
            title="Save changes?"
            confirmLabel="Confirm"
            onConfirm={handleConfirm}
            onCancel={() => setShowConfirm(false)}
            isProcessLoading={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};
